import { nanoid } from 'nanoid';
import {
  BundleAppProxy,
  DynamicBundleItemAppProxy,
  BundleSelection,
  BundleSelectionListParams,
  BundleSelectionsResponse,
  CreateBundleSelectionRequest,
  Session,
  UpdateBundleSelectionRequest,
  UpdateBundlePurchaseItem,
  BundlePurchaseItem,
  BundlePurchaseItemParams,
} from '../types';
import { rechargeApiRequest, shopifyAppProxyRequest } from '../utils/request';
import { getOptions } from '../utils/options';
import { getCDNBundleSettings } from './cdn';
import { toLineItemProperty } from '../utils/bundle';

const STORE_FRONT_MANAGER_URL = '/bundling-storefront-manager';

function getTimestampSecondsFromClient(): number {
  /**
   * Get the current unix epoch in seconds from the client-side.
   */
  return Math.ceil(Date.now() / 1000);
}

async function getTimestampSecondsFromServer(): Promise<number> {
  /**
   * Get the unix epoch from the server instead of using it directly from the
   * client. This must reduce even more the number of invalid Bundles.
   */
  try {
    const { timestamp } = await shopifyAppProxyRequest<{ timestamp: number }>('get', `${STORE_FRONT_MANAGER_URL}/t`, {
      headers: { 'X-Recharge-App': 'storefront-client' },
    });
    return timestamp;
  } catch (ex) {
    console.error(`Fetch failed: ${ex}. Using client-side date.`);
    return getTimestampSecondsFromClient();
  }
}

export async function getBundleId(bundle: BundleAppProxy): Promise<string> {
  const opts = getOptions();
  const isValid = await validateBundle(bundle);
  if (isValid !== true) {
    throw new Error(isValid);
  }
  const timestampSeconds = await getTimestampSecondsFromServer();
  const bundleData = toLineItemProperty({
    variantId: bundle.externalVariantId,
    version: timestampSeconds,
    items: bundle.selections.map(item => {
      return {
        collectionId: item.collectionId,
        productId: item.externalProductId,
        variantId: item.externalVariantId,
        quantity: item.quantity,
        sku: '',
      };
    }),
  });

  try {
    const payload = await shopifyAppProxyRequest<{ id: string; code: number; message: string }>(
      'post',
      `${STORE_FRONT_MANAGER_URL}/api/v1/bundles`,
      {
        data: {
          bundle: bundleData,
        },
        headers: {
          Origin: `https://${opts.storeIdentifier}`,
        },
      }
    );

    if (!payload.id || payload.code !== 200) {
      throw new Error(`1: failed generating rb_id: ${JSON.stringify(payload)}`);
    }

    return payload.id;
  } catch (e) {
    // Handle NetworkError exceptions
    throw new Error(`2: failed generating rb_id ${e}`);
  }
}

export function getDynamicBundleItems(bundle: BundleAppProxy, shopifyProductHandle: string) {
  const isValid = validateDynamicBundle(bundle);
  if (isValid !== true) {
    throw new Error(`Dynamic Bundle is invalid. ${isValid}`);
  }
  // generate unique id for dynamic bundle
  const bundleId = `${nanoid(9)}:${bundle.externalProductId}`;
  return bundle.selections.map(item => {
    const itemData: DynamicBundleItemAppProxy = {
      id: item.externalVariantId,
      quantity: item.quantity,
      properties: {
        _rc_bundle: bundleId,
        _rc_bundle_variant: bundle.externalVariantId,
        _rc_bundle_parent: shopifyProductHandle,
        _rc_bundle_collection_id: item.collectionId,
      },
    };

    if (item.sellingPlan) {
      // this is used by SCI stores
      itemData.selling_plan = item.sellingPlan;
    } else if (item.shippingIntervalFrequency) {
      // this is used by RCS stores
      itemData.properties.shipping_interval_frequency = item.shippingIntervalFrequency;
      itemData.properties.shipping_interval_unit_type = item.shippingIntervalUnitType;
      itemData.id = `${item.discountedVariantId}`;
    }

    return itemData;
  });
}

export async function validateBundle(bundle: BundleAppProxy): Promise<true | string> {
  try {
    // once we implement this function, we can make it raise an exception
    // we could also have a local store relative to this function so we don't have to pass bundleProduct
    if (!bundle) {
      return 'Bundle is not defined';
    }
    const bundleSettings = await getCDNBundleSettings(bundle.externalProductId);
    if (!bundleSettings) {
      return 'Bundle settings do not exist for the given product';
    }
    return true;
  } catch (e) {
    return `Error fetching bundle settings: ${e}`;
  }
}

const intervalUnitGroups = {
  day: ['day', 'days', 'Days'],
  days: ['day', 'days', 'Days'],
  Days: ['day', 'days', 'Days'],
  week: ['week', 'weeks', 'Weeks'],
  weeks: ['week', 'weeks', 'Weeks'],
  Weeks: ['week', 'weeks', 'Weeks'],
  month: ['month', 'months', 'Months'],
  months: ['month', 'months', 'Months'],
  Months: ['month', 'months', 'Months'],
};

/**
 * Validates a dynamic bundle
 *
 * @param bundle Dynamic Bundle being validated
 * @returns true or error message
 */
export function validateDynamicBundle(bundle: BundleAppProxy): true | string {
  if (!bundle) {
    return 'No bundle defined.';
  }
  if (bundle.selections.length === 0) {
    return 'No selections defined.';
  }
  // validation for RCS onetimes
  const { shippingIntervalFrequency, shippingIntervalUnitType } =
    bundle.selections.find(selection => selection.shippingIntervalFrequency || selection.shippingIntervalUnitType) ||
    {};
  if (shippingIntervalFrequency || shippingIntervalUnitType) {
    // if we have shipping intervals then we should have both defined
    if (!shippingIntervalFrequency || !shippingIntervalUnitType) {
      return 'Shipping intervals do not match on selections.';
    } else {
      // if we have shipping intervals then any that are defined should match
      const shippingIntervalUnitGroup = intervalUnitGroups[shippingIntervalUnitType];
      for (let x = 0; x < bundle.selections.length; x++) {
        const { shippingIntervalFrequency: frequency, shippingIntervalUnitType: unitType } = bundle.selections[x];
        if (
          (frequency && frequency !== shippingIntervalFrequency) ||
          (unitType && !shippingIntervalUnitGroup.includes(unitType))
        ) {
          return 'Shipping intervals do not match on selections.';
        }
      }
    }
  }
  return true;
}

export async function getBundleSelection(session: Session, id: string | number): Promise<BundleSelection> {
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'get',
    `/bundle_selections`,
    {
      id,
    },
    session
  );
  return bundle_selection;
}

export function listBundleSelections(
  session: Session,
  query?: BundleSelectionListParams
): Promise<BundleSelectionsResponse> {
  return rechargeApiRequest<BundleSelectionsResponse>('get', `/bundle_selections`, { query }, session);
}

export async function createBundleSelection(
  session: Session,
  createRequest: CreateBundleSelectionRequest
): Promise<BundleSelection> {
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'post',
    `/bundle_selections`,
    {
      data: createRequest,
    },
    session
  );
  return bundle_selection;
}

export async function updateBundleSelection(
  session: Session,
  id: string | number,
  updateRequest: UpdateBundleSelectionRequest
): Promise<BundleSelection> {
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'put',
    `/bundle_selections`,
    {
      id,
      data: updateRequest,
    },
    session
  );
  return bundle_selection;
}

export function deleteBundleSelection(session: Session, id: string | number): Promise<void> {
  return rechargeApiRequest<void>(
    'delete',
    `/bundle_selections`,
    {
      id,
    },
    session
  );
}

export async function updateBundle(
  session: Session,
  purchase_item_id: string | number,
  updateRequest: UpdateBundlePurchaseItem,
  query?: BundlePurchaseItemParams
): Promise<BundlePurchaseItem> {
  const { subscription } = await rechargeApiRequest<{ subscription: BundlePurchaseItem }>(
    'put',
    '/bundles',
    {
      id: purchase_item_id,
      data: updateRequest,
      query,
    },
    session
  );

  return subscription;
}
